/**
 * Hotel Group Management Page
 * 
 * Single page for managing a group of hotels:
 * - Aggregated stats with date filtering
 * - Property comparison
 * - All users across properties
 * - Bulk settings
 * - List of hotels (click → Hotel Management Page)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hotelGroupsAPI, hotelsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Building2,
  ArrowLeft,
  Loader2,
  Save,
  Plus,
  Edit,
  Trash2,
  Users,
  Settings,
  BarChart3,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
  Layers,
  Hotel,
  Activity,
  ClipboardCheck,
  Star,
  TrendingUp,
  TrendingDown,
  DoorOpen,
  Link2,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { Switch } from '../../components/ui/switch';

export default function HotelGroupManagement() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  
  // Core data
  const [group, setGroup] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [allHotels, setAllHotels] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Date range for stats
  const [dateRange, setDateRange] = useState('week');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  
  // Edit mode
  const [editingInfo, setEditingInfo] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  
  // Add hotel dialog
  const [addHotelDialogOpen, setAddHotelDialogOpen] = useState(false);
  const [selectedHotelToAdd, setSelectedHotelToAdd] = useState('');
  
  // Bulk actions state
  const [bulkSurveyEnabled, setBulkSurveyEnabled] = useState(false);
  const [bulkReviewsEnabled, setBulkReviewsEnabled] = useState(false);
  const [bulkReviewUrls, setBulkReviewUrls] = useState({
    google: '',
    tripadvisor: '',
    yelp: '',
    facebook: '',
  });
  const [bulkDefaultPlatform, setBulkDefaultPlatform] = useState('google');
  const [applyingBulk, setApplyingBulk] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupRes, allHotelsRes] = await Promise.all([
        hotelGroupsAPI.get(groupId),
        hotelsAPI.list(),
      ]);
      
      const groupData = groupRes.data;
      setGroup(groupData);
      setAllHotels(allHotelsRes.data || []);
      
      // Filter hotels in this group
      const groupHotels = (allHotelsRes.data || []).filter(
        h => groupData.hotel_ids?.includes(h.id)
      );
      setHotels(groupHotels);
      
      setGroupForm({
        name: groupData.name || '',
        description: groupData.description || '',
      });
      
      // Fetch stats and users
      await Promise.all([
        fetchStats(groupData),
        fetchUsers(),
      ]);
    } catch (error) {
      toast.error('Failed to load group data');
      navigate('/hotels/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  const fetchStats = async (groupData = group) => {
    if (!groupData) return;
    try {
      let days = 7;
      if (dateRange === 'month') days = 30;
      else if (dateRange === 'custom' && customStartDate && customEndDate) {
        days = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));
      }
      
      const statsRes = await hotelGroupsAPI.getStats(groupId, days);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRes = await hotelGroupsAPI.getUsers(groupId);
      setUsers(usersRes.data?.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (group) {
      fetchStats();
    }
  }, [dateRange, customStartDate, customEndDate]);

  // Save group info
  const saveGroupInfo = async () => {
    setSaving(true);
    try {
      await hotelGroupsAPI.update(groupId, groupForm);
      setGroup({ ...group, ...groupForm });
      setEditingInfo(false);
      toast.success('Group updated');
    } catch (error) {
      toast.error('Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  // Add hotel to group
  const addHotelToGroup = async () => {
    if (!selectedHotelToAdd) return;
    setSaving(true);
    try {
      await hotelGroupsAPI.addHotel(groupId, selectedHotelToAdd);
      toast.success('Hotel added to group');
      setAddHotelDialogOpen(false);
      setSelectedHotelToAdd('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add hotel');
    } finally {
      setSaving(false);
    }
  };

  // Remove hotel from group
  const removeHotelFromGroup = async (hotelId) => {
    if (!window.confirm('Remove this hotel from the group?')) return;
    try {
      await hotelGroupsAPI.removeHotel(groupId, hotelId);
      setHotels(hotels.filter(h => h.id !== hotelId));
      toast.success('Hotel removed from group');
    } catch (error) {
      toast.error('Failed to remove hotel');
    }
  };

  // Apply bulk engagement settings to all hotels in group
  const applyBulkEngagementSettings = async () => {
    if (hotels.length === 0) {
      toast.error('No hotels in this group');
      return;
    }
    
    if (!window.confirm(`Apply these settings to all ${hotels.length} hotels in this group?`)) {
      return;
    }
    
    setApplyingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const hotel of hotels) {
      try {
        await hotelsAPI.update(hotel.id, {
          engagement_modes: {
            in_room_survey: { enabled: bulkSurveyEnabled },
            reviews: { enabled: bulkReviewsEnabled },
          },
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to update ${hotel.name}:`, error);
        failCount++;
      }
    }
    
    setApplyingBulk(false);
    
    if (failCount === 0) {
      toast.success(`Settings applied to all ${successCount} hotels`);
    } else {
      toast.warning(`Applied to ${successCount} hotels, ${failCount} failed`);
    }
    
    fetchData();
  };

  // Apply bulk review URLs to all hotels in group
  const applyBulkReviewUrls = async () => {
    if (hotels.length === 0) {
      toast.error('No hotels in this group');
      return;
    }
    
    const hasUrls = Object.values(bulkReviewUrls).some(url => url?.trim());
    if (!hasUrls) {
      toast.error('Please enter at least one review URL');
      return;
    }
    
    if (!window.confirm(`Apply these Review URLs to all ${hotels.length} hotels in this group?`)) {
      return;
    }
    
    setApplyingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const hotel of hotels) {
      try {
        const googleUrl = bulkReviewUrls.google || hotel.engagement_modes?.reviews?.google_url || '';
        const tripadvisorUrl = bulkReviewUrls.tripadvisor || hotel.engagement_modes?.reviews?.tripadvisor_url || '';
        const yelpUrl = bulkReviewUrls.yelp || hotel.engagement_modes?.reviews?.yelp_url || '';
        const facebookUrl = bulkReviewUrls.facebook || hotel.engagement_modes?.reviews?.facebook_url || '';
        
        await hotelsAPI.update(hotel.id, {
          engagement_modes: {
            ...hotel.engagement_modes,
            reviews: {
              ...hotel.engagement_modes?.reviews,
              enabled: true,
              google_url: googleUrl,
              tripadvisor_url: tripadvisorUrl,
              yelp_url: yelpUrl,
              facebook_url: facebookUrl,
              default_platform: bulkDefaultPlatform,
            },
            // Also save to tap_engine_urls for backwards compatibility
            tap_engine_urls: {
              google_reviews_url: googleUrl,
              tripadvisor_url: tripadvisorUrl,
              yelp_url: yelpUrl,
              facebook_url: facebookUrl,
              default_platform: bulkDefaultPlatform,
            },
          },
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to update ${hotel.name}:`, error);
        failCount++;
      }
    }
    
    setApplyingBulk(false);
    
    if (failCount === 0) {
      toast.success(`Review URLs applied to all ${successCount} hotels`);
    } else {
      toast.warning(`Applied to ${successCount} hotels, ${failCount} failed`);
    }
    
    fetchData();
  };

  const getDateRangeLabel = () => {
    if (dateRange === 'week') return 'This Week';
    if (dateRange === 'month') return 'This Month';
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`;
    }
    return 'Select Range';
  };

  // Get hotels not in this group
  const availableHotels = allHotels.filter(h => !group?.hotel_ids?.includes(h.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)]">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] bg-[hsl(217,19%,12%)] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/hotels/groups')}
                className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">All Groups</span>
              </button>
              <div className="h-6 w-px bg-[hsl(217,19%,27%)]" />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">{group.name}</h1>
                  <p className="text-xs text-[hsl(215,20%,65%)]">{hotels.length} properties</p>
                </div>
              </div>
            </div>
            
            {/* Date Range Selector */}
            <div className="flex items-center space-x-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {getDateRangeLabel()}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]" align="end">
                  <div className="p-3 space-y-2">
                    <Button
                      variant={dateRange === 'week' ? 'default' : 'ghost'}
                      className="w-full justify-start"
                      onClick={() => setDateRange('week')}
                    >
                      This Week
                    </Button>
                    <Button
                      variant={dateRange === 'month' ? 'default' : 'ghost'}
                      className="w-full justify-start"
                      onClick={() => setDateRange('month')}
                    >
                      This Month
                    </Button>
                    <div className="border-t border-[hsl(217,19%,27%)] pt-2 mt-2">
                      <p className="text-xs text-[hsl(215,20%,65%)] mb-2">Custom Range</p>
                      <div className="space-y-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full justify-start text-left">
                              {customStartDate ? format(customStartDate, 'MMM d, yyyy') : 'Start Date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                            <Calendar
                              mode="single"
                              selected={customStartDate}
                              onSelect={(date) => {
                                setCustomStartDate(date);
                                if (date && customEndDate) setDateRange('custom');
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full justify-start text-left">
                              {customEndDate ? format(customEndDate, 'MMM d, yyyy') : 'End Date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                            <Calendar
                              mode="single"
                              selected={customEndDate}
                              onSelect={(date) => {
                                setCustomEndDate(date);
                                if (customStartDate && date) setDateRange('custom');
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        
        {/* Aggregated Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_hotels || hotels.length}</p>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Hotels</p>
                </div>
                <Hotel className="w-8 h-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_rooms || 0}</p>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Total Rooms</p>
                </div>
                <DoorOpen className="w-8 h-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_taps || 0}</p>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Total Taps</p>
                </div>
                <Activity className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_surveys || 0}</p>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Surveys</p>
                </div>
                <ClipboardCheck className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_reviews || 0}</p>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Reviews</p>
                </div>
                <Star className="w-8 h-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="hotels" className="space-y-4">
          <TabsList className="bg-[hsl(217,19%,12%)] border border-[hsl(217,19%,27%)] p-1">
            <TabsTrigger value="hotels" className="data-[state=active]:bg-purple-600">
              <Hotel className="w-4 h-4 mr-2" />
              Hotels ({hotels.length})
            </TabsTrigger>
            <TabsTrigger value="bulk" className="data-[state=active]:bg-orange-600">
              <Zap className="w-4 h-4 mr-2" />
              Bulk Actions
            </TabsTrigger>
            <TabsTrigger value="comparison" className="data-[state=active]:bg-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Comparison
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-green-600">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-amber-600">
              <Settings className="w-4 h-4 mr-2" />
              Group Settings
            </TabsTrigger>
          </TabsList>

          {/* Hotels Tab */}
          <TabsContent value="hotels" className="space-y-4">
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-white flex items-center">
                    <Hotel className="w-5 h-5 mr-2 text-purple-500" />
                    Hotels in Group
                  </CardTitle>
                  <CardDescription className="text-[hsl(215,20%,65%)]">
                    Click on a hotel to manage it individually
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setAddHotelDialogOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={availableHotels.length === 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Hotel
                </Button>
              </CardHeader>
              <CardContent>
                {hotels.length === 0 ? (
                  <div className="text-center py-8">
                    <Hotel className="w-12 h-12 text-[hsl(215,20%,45%)] mx-auto mb-3" />
                    <p className="text-[hsl(215,20%,65%)]">No hotels in this group yet.</p>
                    <Button
                      onClick={() => setAddHotelDialogOpen(true)}
                      className="mt-4 bg-purple-600 hover:bg-purple-700"
                      disabled={availableHotels.length === 0}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Hotel
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {hotels.map((hotel) => {
                      const hotelStats = stats?.hotels?.find(h => h.id === hotel.id);
                      return (
                        <div
                          key={hotel.id}
                          className="p-4 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)] hover:border-purple-500/50 transition-colors group"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div 
                              className="flex items-center space-x-3 cursor-pointer flex-1"
                              onClick={() => navigate(`/hotels/${hotel.id}/manage`)}
                            >
                              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-blue-500" />
                              </div>
                              <div>
                                <p className="font-medium text-white group-hover:text-purple-400 transition-colors">
                                  {hotel.name}
                                </p>
                                <p className="text-xs text-[hsl(215,20%,65%)]">
                                  {hotel.city || hotel.hotel_code || 'No location'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => navigate(`/hotels/${hotel.id}/manage`)}
                                className="p-1.5 rounded hover:bg-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white"
                                title="Manage Hotel"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeHotelFromGroup(hotel.id)}
                                className="p-1.5 rounded hover:bg-red-500/20 text-[hsl(215,20%,65%)] hover:text-red-400"
                                title="Remove from Group"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Quick Stats */}
                          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[hsl(217,19%,27%)]">
                            <div className="text-center">
                              <p className="text-lg font-semibold text-white">{hotelStats?.total_rooms || hotel.total_rooms || 0}</p>
                              <p className="text-xs text-[hsl(215,20%,65%)]">Rooms</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-green-400">{hotelStats?.taps || 0}</p>
                              <p className="text-xs text-[hsl(215,20%,65%)]">Taps</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-blue-400">{hotelStats?.surveys || 0}</p>
                              <p className="text-xs text-[hsl(215,20%,65%)]">Surveys</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Actions Tab */}
          <TabsContent value="bulk" className="space-y-4">
            {/* Bulk Engagement Settings */}
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-orange-500" />
                  Bulk Engagement Settings
                </CardTitle>
                <CardDescription className="text-[hsl(215,20%,65%)]">
                  Apply these settings to all {hotels.length} hotels in this group at once
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {hotels.length === 0 ? (
                  <div className="text-center py-8 text-[hsl(215,20%,65%)]">
                    Add hotels to this group first to use bulk actions
                  </div>
                ) : (
                  <>
                    {/* Guest Arrival Tags Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                          <ClipboardCheck className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Guest Arrival Tags</p>
                          <p className="text-sm text-[hsl(215,20%,65%)]">
                            Enable in-room survey tags for all hotels
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={bulkSurveyEnabled}
                        onCheckedChange={setBulkSurveyEnabled}
                      />
                    </div>
                    
                    {/* Review Engine Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                          <Star className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Review Engine Cards</p>
                          <p className="text-sm text-[hsl(215,20%,65%)]">
                            Enable review redirect cards for all hotels
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={bulkReviewsEnabled}
                        onCheckedChange={setBulkReviewsEnabled}
                      />
                    </div>
                    
                    {/* Apply Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={applyBulkEngagementSettings}
                        disabled={applyingBulk}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {applyingBulk ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Apply to All {hotels.length} Hotels
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Bulk Review URLs */}
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Link2 className="w-5 h-5 mr-2 text-amber-500" />
                  Bulk Review URLs
                </CardTitle>
                <CardDescription className="text-[hsl(215,20%,65%)]">
                  Set the same review platform URLs for all hotels. Leave blank to keep existing URLs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {hotels.length === 0 ? (
                  <div className="text-center py-8 text-[hsl(215,20%,65%)]">
                    Add hotels to this group first to use bulk actions
                  </div>
                ) : (
                  <>
                    {/* Google */}
                    <div className="space-y-2">
                      <Label className="text-white">Google Reviews URL</Label>
                      <Input
                        placeholder="https://g.page/r/your-hotel/review"
                        value={bulkReviewUrls.google}
                        onChange={(e) => setBulkReviewUrls({ ...bulkReviewUrls, google: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    
                    {/* TripAdvisor */}
                    <div className="space-y-2">
                      <Label className="text-white">TripAdvisor URL</Label>
                      <Input
                        placeholder="https://www.tripadvisor.com/..."
                        value={bulkReviewUrls.tripadvisor}
                        onChange={(e) => setBulkReviewUrls({ ...bulkReviewUrls, tripadvisor: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    
                    {/* Facebook */}
                    <div className="space-y-2">
                      <Label className="text-white">Facebook URL</Label>
                      <Input
                        placeholder="https://facebook.com/your-hotel/reviews"
                        value={bulkReviewUrls.facebook}
                        onChange={(e) => setBulkReviewUrls({ ...bulkReviewUrls, facebook: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    
                    {/* Yelp */}
                    <div className="space-y-2">
                      <Label className="text-white">Yelp URL</Label>
                      <Input
                        placeholder="https://www.yelp.com/biz/..."
                        value={bulkReviewUrls.yelp}
                        onChange={(e) => setBulkReviewUrls({ ...bulkReviewUrls, yelp: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    
                    {/* Default Platform */}
                    <div className="space-y-2 pt-4 border-t border-[hsl(217,19%,27%)]">
                      <Label className="text-white">Default Platform</Label>
                      <Select value={bulkDefaultPlatform} onValueChange={setBulkDefaultPlatform}>
                        <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                          <SelectItem value="google">Google</SelectItem>
                          <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
                          <SelectItem value="facebook">Facebook</SelectItem>
                          <SelectItem value="yelp">Yelp</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Apply Button */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={applyBulkReviewUrls}
                        disabled={applyingBulk}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {applyingBulk ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4 mr-2" />
                        )}
                        Apply URLs to All {hotels.length} Hotels
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-4">
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
                  Property Comparison
                </CardTitle>
                <CardDescription className="text-[hsl(215,20%,65%)]">
                  Compare performance across all hotels in this group
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hotels.length === 0 ? (
                  <div className="text-center py-8 text-[hsl(215,20%,65%)]">
                    Add hotels to see comparison data
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[hsl(217,19%,27%)]">
                          <th className="text-left py-3 px-4 text-[hsl(215,20%,65%)] font-medium">Hotel</th>
                          <th className="text-right py-3 px-4 text-[hsl(215,20%,65%)] font-medium">Rooms</th>
                          <th className="text-right py-3 px-4 text-[hsl(215,20%,65%)] font-medium">Taps</th>
                          <th className="text-right py-3 px-4 text-[hsl(215,20%,65%)] font-medium">Surveys</th>
                          <th className="text-right py-3 px-4 text-[hsl(215,20%,65%)] font-medium">Conversion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats?.hotels || hotels).map((hotel) => {
                          const hotelData = stats?.hotels?.find(h => h.id === hotel.id) || hotel;
                          const taps = hotelData.taps || 0;
                          const surveys = hotelData.surveys || 0;
                          const conversion = taps > 0 ? Math.round((surveys / taps) * 100) : 0;
                          
                          return (
                            <tr 
                              key={hotel.id} 
                              className="border-b border-[hsl(217,19%,27%)] hover:bg-[hsl(217,19%,17%)] cursor-pointer"
                              onClick={() => navigate(`/hotels/${hotel.id}/manage`)}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-blue-500" />
                                  </div>
                                  <span className="text-white font-medium">{hotelData.name || hotel.name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-white">{hotelData.total_rooms || hotel.total_rooms || 0}</td>
                              <td className="py-3 px-4 text-right text-green-400">{taps}</td>
                              <td className="py-3 px-4 text-right text-blue-400">{surveys}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  conversion >= 50 ? 'bg-green-500/20 text-green-400' :
                                  conversion >= 25 ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {conversion}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Users className="w-5 h-5 mr-2 text-green-500" />
                  All Users
                </CardTitle>
                <CardDescription className="text-[hsl(215,20%,65%)]">
                  Staff members across all hotels in this group
                </CardDescription>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-[hsl(215,20%,45%)] mx-auto mb-3" />
                    <p className="text-[hsl(215,20%,65%)]">No users found across group hotels.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <span className="text-green-400 font-medium">
                              {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name || user.email}</p>
                            <p className="text-sm text-[hsl(215,20%,65%)]">
                              {user.role || 'Staff'} • {user.hotel_count || 0} hotels
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.is_active !== false 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Group Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center">
                    <Settings className="w-5 h-5 mr-2 text-amber-500" />
                    Group Information
                  </CardTitle>
                  <CardDescription className="text-[hsl(215,20%,65%)]">
                    Basic information about this hotel group
                  </CardDescription>
                </div>
                {!editingInfo && (
                  <Button
                    variant="outline"
                    onClick={() => setEditingInfo(true)}
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingInfo ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white">Group Name *</Label>
                      <Input
                        value={groupForm.name}
                        onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Description</Label>
                      <Input
                        value={groupForm.description}
                        onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                        className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingInfo(false);
                          setGroupForm({ name: group.name, description: group.description || '' });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={saveGroupInfo}
                        disabled={saving}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-[hsl(215,20%,65%)]">Group Name</p>
                      <p className="text-white text-lg">{group.name}</p>
                    </div>
                    {group.description && (
                      <div>
                        <p className="text-sm text-[hsl(215,20%,65%)]">Description</p>
                        <p className="text-white">{group.description}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-[hsl(215,20%,65%)]">Hotels in Group</p>
                      <p className="text-white">{hotels.length} properties</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Hotel Dialog */}
      <Dialog open={addHotelDialogOpen} onOpenChange={setAddHotelDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <DialogHeader>
            <DialogTitle className="text-white">Add Hotel to Group</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              Select a hotel to add to this group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableHotels.length === 0 ? (
              <p className="text-[hsl(215,20%,65%)] text-center py-4">
                All hotels are already in this group.
              </p>
            ) : (
              <Select value={selectedHotelToAdd} onValueChange={setSelectedHotelToAdd}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue placeholder="Select a hotel" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                  {availableHotels.map((hotel) => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      {hotel.name} {hotel.city && `(${hotel.city})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddHotelDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={addHotelToGroup} 
              disabled={saving || !selectedHotelToAdd} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Hotel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
